import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { fromWire } from '@core/filters/path-filter/codec';
import { countDraftConditions } from '@core/filters/path-filter/draft';
import { printFilter } from '@core/filters/path-filter/dsl/printer';
import { TemplateValues, templateValueText } from '@core/filters/path-filter/templates';
import { FilterExpr } from '@core/filters/path-filter/wire';
import { FilterPresetModel, FilterPresetScope } from '@core/models/filter-preset.model';
import { ICONS } from '@core/services/icons/icons.service';
import { PathFilterVocabularyService } from '@core/services/path-filter-vocabulary/path-filter-vocabulary.service';
import { extractGbeErrorMessage } from '@core/utils/extract-gbe-error';
import { foldForSearch } from '@core/utils/search-text.utils';
import { isNil } from '@core/utils/shared-utils';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { DropdownComponent } from '@shared/components/dropdown/dropdown.component';
import { FilterOperationsService } from '@shared/components/filter-query-builder/filter-operations.service';
import {
  FilterCondition,
  FilterGroup,
  FilterOperation
} from '@shared/components/filter-query-builder/filter.models';
import { InputComponent } from '@shared/components/input/input.component';
import {
  ModalComponent,
  ModalConfig,
  ModalType
} from '@shared/components/modal/modal.component';
import { DslTextComponent } from '@shared/components/path-filter/dsl-text/dsl-text.component';
import { countFilterConditions } from '@shared/utils/filter-preset.utils';

import { FilterPresetsStore } from './filter-presets.store';
import { FilterTemplateActivationComponent } from './filter-template-activation/filter-template-activation.component';

type DialogMode = 'create' | 'rename' | 'delete';

const NAME_MAX_LENGTH = 80;

/** Whitespace-only names are reported as `required`, so the field shows one message. */
const nonBlankValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null =>
  String(control.value ?? '').trim().length === 0 ? { required: true } : null;

/** What the dialog is doing; the values it edits live in the form, not here. */
interface DialogState {
  mode: DialogMode;
  preset?: FilterPresetModel;
  error: string | null;
  saving: boolean;
}

/** One saved criterion, rendered as a key / operator / value pill in the save dialog. */
interface CriterionSummary {
  field: string;
  operation: string;
  value: string | null;
}

/** One sorted column, rendered as a label / arrow pill in the save dialog. */
interface SortSummary {
  field: string;
  arrow: string;
}

function countStoredTree(tree: FilterExpr): number | null {
  try {
    return countDraftConditions(fromWire(tree));
  } catch (error) {
    if (error instanceof TypeError) return null;
    throw error;
  }
}

@Component({
  selector: 'app-filter-presets',
  imports: [
    NgTemplateOutlet,
    ReactiveFormsModule,
    TranslatePipe,
    CheckboxComponent,
    DropdownComponent,
    DslTextComponent,
    FilterTemplateActivationComponent,
    InputComponent,
    ModalComponent
  ],
  templateUrl: './filter-presets.component.html',
  host: { class: 'inline-flex shrink-0 items-center' },
  styles: ``
})
export class FilterPresetsComponent {
  protected readonly presetsStore = inject(FilterPresetsStore);
  protected readonly vocabulary = inject(PathFilterVocabularyService).vocabularyFor(
    this.presetsStore.entityType
  );
  private readonly operations = inject(FilterOperationsService);
  private readonly translate = inject(TranslateService);
  private readonly formBuilder = inject(FormBuilder);

  protected readonly ICONS = ICONS;
  protected readonly FilterPresetScope = FilterPresetScope;

  protected readonly isMenuOpen = signal(false);
  /** The chip wears its accent colours both while open and while a preset is applied. */
  protected readonly isChipHighlighted = computed(
    () => !isNil(this.presetsStore.activePreset()) || this.isMenuOpen()
  );
  /** "‹template name› · ‹values›", values in parameter order, date-times in local time. */
  protected readonly templatePillLabel = computed(() => {
    const active = this.presetsStore.activeTemplate();
    if (isNil(active)) {
      return '';
    }
    const values = (active.preset.parameters ?? []).map(({ name, kind }) =>
      templateValueText(kind, active.values[name])
    );
    return [active.preset.name, ...values].join(' · ');
  });

  /** Prefills the popover when it reopens on the template already applied. */
  protected readonly activationValues = computed<TemplateValues>(() => {
    const pending = this.presetsStore.pendingTemplate();
    const active = this.presetsStore.activeTemplate();
    return !isNil(pending) && active?.preset.id === pending.id ? active.values : {};
  });

  protected readonly closeActivation = (): void => this.presetsStore.cancelActivation();

  protected readonly searchTerm = signal('');
  protected readonly openRowMenuId = signal<number | null>(null);
  protected readonly dialog = signal<DialogState | null>(null);
  protected readonly busy = signal(false);

  /** Everything the create and rename dialogs edit. */
  protected readonly dialogForm = this.formBuilder.nonNullable.group({
    name: ['', [nonBlankValidator, Validators.maxLength(NAME_MAX_LENGTH)]],
    shareWithDataspace: [false],
    asDefault: [false],
    includeSort: [false],
    contextScoped: [true]
  });

  protected readonly setIncludeSort = (checked: boolean): void =>
    this.dialogForm.controls.includeSort.setValue(checked);
  protected readonly setContextScoped = (checked: boolean): void =>
    this.dialogForm.controls.contextScoped.setValue(checked);
  protected readonly setShareWithDataspace = (checked: boolean): void =>
    this.dialogForm.controls.shareWithDataspace.setValue(checked);
  protected readonly setAsDefault = (checked: boolean): void =>
    this.dialogForm.controls.asDefault.setValue(checked);

  private readonly dialogFormStatus = toSignal(this.dialogForm.statusChanges, {
    initialValue: this.dialogForm.status
  });

  protected readonly canSubmitDialog = computed(() => {
    const dialog = this.dialog();
    if (isNil(dialog) || dialog.saving) {
      return false;
    }
    return dialog.mode === 'delete' || this.dialogFormStatus() === 'VALID';
  });

  protected readonly dialogPresetName = computed(() => this.dialog()?.preset?.name ?? '');

  constructor() {
    this.dialogForm.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() =>
        this.dialog.update((dialog) =>
          isNil(dialog) || isNil(dialog.error) ? dialog : { ...dialog, error: null }
        )
      );
  }

  private readonly matchesSearch = (preset: FilterPresetModel): boolean =>
    foldForSearch(preset.name).includes(foldForSearch(this.searchTerm()));

  protected readonly groups = computed(() =>
    this.presetsStore
      .presetGroups()
      .map((group) => ({ ...group, presets: group.presets.filter(this.matchesSearch) }))
      .filter((group) => group.presets.length > 0)
  );
  protected readonly hasResults = computed(() => this.groups().length > 0);

  /** Criteria of the live filters, for the save dialog's recap. */
  protected readonly currentCriteria = computed<CriterionSummary[]>(() => {
    const filters = this.presetsStore.currentPayload().filters;
    if (isNil(filters)) {
      return [];
    }
    const fieldLabel = (field: string): string =>
      this.presetsStore
        .availableFields()
        .find((available) => (available.dbColumnName || available.field) === field)
        ?.label ?? field;

    const collect = (group: FilterGroup): CriterionSummary[] =>
      group.conditions.flatMap((item) =>
        'operation' in item
          ? [
              {
                field: fieldLabel(item.field),
                operation: this.operations.getOperationConfig(item.operation).label,
                value: this.conditionValue(item)
              }
            ]
          : collect(item)
      );

    return collect(filters.rootGroup);
  });

  private conditionValue(condition: FilterCondition): string | null {
    if (
      condition.operation === FilterOperation.IS_EMPTY ||
      condition.operation === FilterOperation.IS_NOT_EMPTY ||
      condition.operation === FilterOperation.IS_TRUE ||
      condition.operation === FilterOperation.IS_FALSE
    ) {
      return null;
    }
    const candidate = condition as {
      value?: unknown;
      values?: unknown[];
      labelValue?: string;
      labelValues?: string[];
      tagNames?: string[];
    };
    const parts =
      candidate.values ??
      candidate.labelValues ??
      candidate.tagNames ??
      (isNil(candidate.labelValue) ? undefined : [candidate.labelValue]) ??
      (isNil(candidate.value) ? undefined : [candidate.value]);

    return isNil(parts) || parts.length === 0 ? '—' : parts.join(', ');
  }

  /**
   * The live sort, for the save dialog's recap. Resolved through the sortable columns so
   * the user reads a header label rather than the DB column name that gets stored.
   */
  protected readonly currentSortSummary = computed<SortSummary[]>(() => {
    const sort = this.presetsStore.currentPayload().sort ?? [];
    const fields = this.presetsStore.sortableFields();
    return sort.map((entry) => ({
      field:
        fields.find(
          (field) => field.dbColName === entry.field && field.labelKey === entry.labelKey
        )?.label ?? entry.field,
      arrow: entry.order === 'desc' ? '↓' : '↑'
    }));
  });

  /**
   * By preset id. Decoding a Path tree per change-detection pass would rebuild every draft. A
   * template shows its parameter count instead, so its tree, which gbe stores unchecked, is never
   * decoded here; any other tree the codec cannot walk counts as `null` rather than taking the
   * whole menu down with it.
   */
  protected readonly criteriaCounts = computed(
    () =>
      new Map(
        this.presetsStore
          .presets()
          .filter((preset) => !preset.template)
          .map((preset) => [
            preset.id,
            isNil(preset.pathFilter)
              ? countFilterConditions(preset.filters)
              : countStoredTree(preset.pathFilter)
          ])
      )
  );

  /** The live Path filter as DSL, for the save dialog's recap. */
  protected readonly currentPathFilterText = computed(() => {
    const tree = this.presetsStore.currentPayload().pathFilter;
    return isNil(tree) ? null : printFilter(tree);
  });

  protected readonly currentCriteriaCount = computed(() => {
    const tree = this.presetsStore.currentPayload().pathFilter;
    return isNil(tree)
      ? this.currentCriteria().length
      : countDraftConditions(fromWire(tree));
  });

  protected hasSort(preset: FilterPresetModel): boolean {
    return (preset.sort?.length ?? 0) > 0;
  }

  protected readonly dialogConfig = computed<ModalConfig>(() => {
    const dialog = this.dialog();
    const isDelete = dialog?.mode === 'delete';
    return {
      dismissible: true,
      askingModal: true,
      closeOnBackdropClick: true,
      type: isDelete ? ModalType.ERROR : ModalType.BASIC,
      actions: [
        {
          label: this.translate.instant('common.cancel'),
          color: 'cancel',
          callback: () => this.closeDialog()
        },
        {
          label: this.translate.instant(
            dialog?.saving ? 'common.saving' : this.dialogSubmitKey(dialog?.mode)
          ),
          color: isDelete ? 'danger' : 'primary',
          icon: isDelete ? 'trash' : 'check',
          disabled: !this.canSubmitDialog(),
          callback: () => this.submitDialog()
        }
      ]
    };
  });

  private dialogSubmitKey(mode?: DialogMode): string {
    switch (mode) {
      case 'delete':
        return 'common.delete';
      case 'rename':
        return 'common.rename';
      default:
        return 'common.save';
    }
  }

  protected dialogTitleKey(mode: DialogMode): string {
    switch (mode) {
      case 'create':
        return 'features.data.filter_presets.dialog.create_title';
      case 'rename':
        return 'features.data.filter_presets.dialog.rename_title';
      case 'delete':
        return 'features.data.filter_presets.dialog.delete_title';
    }
  }

  protected toggleMenu(): void {
    this.isMenuOpen.update((open) => !open);
    this.searchTerm.set('');
  }

  protected readonly closeMenu = (): void => {
    this.isMenuOpen.set(false);
    this.openRowMenuId.set(null);
  };

  protected toggleRowMenu(preset: FilterPresetModel, event: Event): void {
    event.stopPropagation();
    this.openRowMenuId.update((id) => (id === preset.id ? null : preset.id));
  }

  protected readonly closeRowMenu = (): void => {
    this.openRowMenuId.set(null);
  };

  protected canWrite(preset: FilterPresetModel): boolean {
    return (
      preset.scope === FilterPresetScope.USER || this.presetsStore.canShareWithDataspace()
    );
  }

  protected applyPreset(preset: FilterPresetModel): void {
    this.presetsStore.apply(preset);
    this.closeMenu();
  }

  protected detachPreset(): void {
    this.presetsStore.detach();
    this.closeMenu();
  }

  protected saveOver(preset: FilterPresetModel): void {
    if (this.busy()) {
      return;
    }
    this.busy.set(true);
    this.closeMenu();
    this.presetsStore.saveOver(preset).subscribe({
      next: () => this.busy.set(false),
      error: () => this.busy.set(false)
    });
  }

  protected toggleDefault(preset: FilterPresetModel, event: Event): void {
    event.stopPropagation();
    if (this.busy()) {
      return;
    }
    this.busy.set(true);
    this.closeRowMenu();
    this.presetsStore.toggleDefault(preset).subscribe({
      next: () => this.busy.set(false),
      error: () => this.busy.set(false)
    });
  }

  protected openCreateDialog(): void {
    this.closeMenu();
    this.resetDialogForm('');
    this.dialog.set({ mode: 'create', error: null, saving: false });
  }

  protected openRenameDialog(preset: FilterPresetModel, event: Event): void {
    event.stopPropagation();
    this.closeMenu();
    this.resetDialogForm(preset.name);
    this.dialog.set({ mode: 'rename', preset, error: null, saving: false });
  }

  protected openDeleteDialog(preset: FilterPresetModel, event: Event): void {
    event.stopPropagation();
    this.closeMenu();
    this.dialog.set({ mode: 'delete', preset, error: null, saving: false });
  }

  /** Reopening a dialog starts from a pristine form, so no stale error is shown. */
  private resetDialogForm(name: string): void {
    this.dialogForm.reset({
      name,
      shareWithDataspace: false,
      asDefault: false,
      includeSort: false,
      contextScoped: true
    });
  }

  protected closeDialog(): void {
    this.dialog.set(null);
  }

  protected submitDialog(): void {
    const dialog = this.dialog();
    if (isNil(dialog) || !this.canSubmitDialog()) {
      return;
    }

    const target = dialog.preset;

    if (dialog.mode === 'delete') {
      if (!isNil(target)) {
        this.runDialog(dialog, this.presetsStore.remove(target));
      }
      return;
    }

    const { name, shareWithDataspace, asDefault, includeSort, contextScoped } =
      this.dialogForm.getRawValue();
    const trimmedName = name.trim();

    if (dialog.mode === 'rename') {
      if (!isNil(target)) {
        this.runDialog(dialog, this.presetsStore.rename(target, trimmedName));
      }
      return;
    }

    this.runDialog(
      dialog,
      this.presetsStore.saveNew(
        trimmedName,
        shareWithDataspace ? FilterPresetScope.DATASPACE : FilterPresetScope.USER,
        asDefault,
        includeSort,
        contextScoped
      )
    );
  }

  private runDialog(dialog: DialogState, request$: Observable<unknown>): void {
    this.dialog.set({ ...dialog, saving: true, error: null });
    request$.subscribe({
      next: () => this.closeDialog(),
      error: (err: unknown) =>
        this.dialog.update((current) =>
          current
            ? {
                ...current,
                saving: false,
                error: extractGbeErrorMessage(
                  err,
                  this.translate.instant(
                    'features.data.filter_presets.dialog.generic_error'
                  )
                )
              }
            : current
        )
    });
  }
}
